/*
 * Sudoku engine — the hint: the next deduction FROM THE PLAYER'S OWN POSITION.
 *
 *   SudokuEngine.hint(board)   → { kind, … }
 *
 *   board = { size, values, solution, notes, strictNotes, unique }
 *     values       what stands on the board now (givens and the player's digits)
 *     solution     the proven solution
 *     notes        per cell: the candidates the player still allows (0 = no opinion)
 *     strictNotes  the notes are computed candidates minus what the player crossed out
 *     unique       one solution is proven (it is, for every board we let be played)
 *
 *   kind 'mistake'  a digit on the board is wrong → cells
 *   kind 'note'     the notes of a cell exclude the digit that belongs there → cell, digit
 *   kind 'step'     a deduction → step (see logic.js) with what the page needs to show it:
 *                     focus    where to look first — { type: 'unit' | 'digit' | 'cells', … }
 *                     because  cells whose digits make the deduction work (a single's witnesses)
 *   kind 'done'     nothing left to deduce
 *
 * Why the hint starts from the player's notes: a hint that ignores what the
 * player has already crossed out explains a board the player is not looking at
 * (the complaint in every app review: "the hint confused what I had simplified").
 * A note that removes the right digit cannot be built upon, so it is reported
 * first — no later deduction is sound on top of it.
 */
(function (root) {
    'use strict';

    var S = root.SudokuEngine;
    var POP = S.POP;

    // A filled cell that holds `digit` and sees `cell` — the reason the digit
    // cannot go there.
    function witness(geo, values, cell, digit) {
        var peers = geo.peers[cell];
        for (var k = 0; k < peers.length; k++) if (values[peers[k]] === digit) return peers[k];
        return -1;
    }

    function explain(geo, values, step) {
        var n = geo.n, because = [], k, d;
        var cell = step.cells[0];
        switch (step.tech) {
            case 'full_house':
                step.focus = { type: 'unit', units: [step.unit] };
                break;
            case 'hidden_single':
                // Every other free cell of the unit is closed by the same digit nearby.
                d = step.digits[0];
                var unit = geo.units[step.unit];
                for (k = 0; k < n; k++) {
                    if (unit[k] === cell || values[unit[k]]) continue;
                    var w = witness(geo, values, unit[k], d);
                    if (w >= 0 && because.indexOf(w) < 0) because.push(w);
                }
                step.focus = { type: 'unit', units: [step.unit] };
                break;
            case 'naked_single':
                // One witness for each of the other digits.
                for (d = 1; d <= n; d++) {
                    if (d === step.digits[0]) continue;
                    var seen = witness(geo, values, cell, d);
                    if (seen >= 0) because.push(seen);
                }
                step.focus = { type: 'unit', units: [2 * n + geo.cellBox[cell]] };
                break;
            case 'pointing': case 'claiming':
            case 'naked_pair': case 'naked_triple': case 'naked_quad':
            case 'hidden_pair': case 'hidden_triple': case 'hidden_quad':
                step.focus = { type: 'unit', units: [step.unit] };
                break;
            case 'x_wing': case 'swordfish': case 'jellyfish':
            case 'skyscraper': case 'two_string_kite': case 'simple_coloring':
                step.focus = { type: 'digit', digit: step.digits[0] };
                break;
            default:
                step.focus = { type: 'cells', cells: step.tech === 'forcing' || step.tech === 'brute' ? step.cells : step.cells.slice() };
        }
        step.because = because;
        return step;
    }

    function hint(board) {
        var geo = S.geometry(board.size), values = board.values, solution = board.solution;
        var notes = board.notes || null, i;
        var wrong = [];
        for (i = 0; i < geo.cells; i++) if (values[i] && values[i] !== solution[i]) wrong.push(i);
        if (wrong.length) return { kind: 'mistake', cells: wrong };

        // Hand-written notes are not always "everything this cell may be": some
        // players mark only where a digit can go inside a box. So a cell whose
        // notes miss its digit is a mistake only when the notes are the
        // computed ones (strictNotes — the player crossed the digit out);
        // otherwise that cell's notes are simply not relied upon.
        var masks = null;
        if (notes) {
            masks = [];
            for (i = 0; i < geo.cells; i++) {
                var m = values[i] ? 0 : (notes[i] | 0);
                if (m && !(m & (1 << (solution[i] - 1)))) {
                    if (board.strictNotes) return { kind: 'note', cell: i, digit: solution[i] };
                    m = 0;
                }
                masks.push(m);
            }
        }
        var st = S.state(geo, values, masks);
        st.unique = board.unique !== false;
        if (!st.left) return { kind: 'done' };
        var step = S.nextStep(st, { maxTier: 7, solution: solution });
        if (!step) return { kind: 'done' };
        // What the cells involved may hold right now — the page draws these as
        // candidates even when the player keeps no notes.
        var involved = step.cells.slice();
        (step.elim || []).forEach(function (e) { if (involved.indexOf(e[0]) < 0) involved.push(e[0]); });
        step.cand = involved.map(function (c) { return [c, st.cand[c]]; });
        return { kind: 'step', step: explain(geo, values, step) };
    }

    // The whole road from a position to the solution, one explained step at a
    // time — "solve it for me, but show how".
    function walkthrough(board, limit) {
        var geo = S.geometry(board.size), values = board.values.slice(), out = [];
        var st = S.state(geo, values);
        st.unique = board.unique !== false;
        limit = limit || 400;
        while (st.left && out.length < limit) {
            var step = S.nextStep(st, { maxTier: 7, solution: board.solution });
            if (!step) break;
            var current = Array.prototype.slice.call(st.val);
            out.push(explain(geo, current, step));
            S.applyStep(st, step);
        }
        return out;
    }

    S.hint = hint;
    S.walkthrough = walkthrough;
    S.explain = explain;
    S.candidateCount = function (mask) { return POP[mask]; };
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this));
